import './style.css';
import { useEffect, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {cloneDeep } from 'lodash';
import { confirm, createModal, LoadingIndicator, Tooltip } from '@thermon-platforms/thermon-ui-core';
import { Link } from 'react-router-dom';
import ConfigureDeviceModal from './components/configure-device-modal';
import { getAllChildDevicesByType, getDeviceUrl, isDuo } from 'util/device';
import useDevicesStore from 'store/devices';
import useDeviceTypesStore from 'store/device-types';
import api from 'util/api';
import CircuitRow from './components/circuit-row';


function ManageDevices({device}) {
  const [loading, setLoading] = useState(false);
  const [children, setChildren] = useState(
    cloneDeep(
      // If this device is a Duo, we are ignoring the panel and controller, so let's grab the circuits
      isDuo(device.model.externalId) ?
        getAllChildDevicesByType(device.model, 'Circuit') :
        device.model.children
    )
  );
  const [devicesStore, devicesStoreActions] = useDevicesStore();
  const [deviceTypesStore] = useDeviceTypesStore();
  const [availableTypes, setAvailableTypes] = useState();
  const [selectedType, setSelectedType] = useState();
  const [selectedDeviceModel, setSelectedDeviceModel] = useState();
  const [waitingToEdit, setWaitingToEdit] = useState();
  const ancestorIsDuo = device.path?.some(p => isDuo(p.externalId));

  useEffect(() => {
    if (!deviceTypesStore.types.length) return;
    setLoading(true);
    api(`/devices/compatibleDevices?parentType=${device.model.type}&parentModel=${device.model.deviceModel}&externalId=${device.model.externalId}`)
      .then(async res => {
        const typeIds = await res.json();
        const types = typeIds.map(id => deviceTypesStore.types.find(type => type.id == id));
        setAvailableTypes(types.reduce((acc, type) => {
          // If we've not registered the type yet, let's do that
          if (!acc[type.type]) {
            acc[type.type] = {
              type: type.type,
              id: type.type == type.name ? type.id : undefined,
              children: []
            }
          }

          // Kind of a hacky way to avoid adding generic types to the list
          // unless they are panels
          if (type.type != type.name || type.type == 'Panel') {
            acc[type.type].children.push(type);
          }

          return acc;
        }, {}));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [deviceTypesStore.types, device.model.externalId]);

  async function remove(child) {
    const childIsDuo = isDuo(child.externalId);
    if (!childIsDuo && child.type != 'Controller' && child.children.length) return;
    confirm({
      header: `${t`Are you sure you want to remove`} ${child.name}?`
    })
    .then(async () => {
      const index = children.indexOf(child);
      children.splice(index, 1);
      setChildren([...children]);
      // A Duo Bridge owns a nested Duo Controller that must be deleted first,
      // then the Bridge itself.
      if (childIsDuo) {
        const controller = getAllChildDevicesByType(child, 'Controller')[0];
        if (controller) {
          await devicesStoreActions.remove(controller);
        }
      }
      devicesStoreActions.remove(child);
    })
    .catch(() => {/* no-op */})
  }

  function add() {
    // If the selectedType has no children, it's generic so let's use it
    // Otherwise, try to use the selected model.
    const deviceModel = availableTypes[selectedType]?.children.length ? selectedDeviceModel : availableTypes[selectedType].id;
    if (!deviceModel) return;
    const configureDeviceModal = createModal(
      <ConfigureDeviceModal
        device={{
          name: undefined,
          type: selectedType,
          deviceModel,
          coordinates: {},
          configuration: []
        }}
        parent={device.model}
      />
    );

    configureDeviceModal.open()
      .catch(() => {/* no-op */});
  }

  async function edit(child) {
    setWaitingToEdit(child.id);
    const detail = await devicesStoreActions.fetchDetail(child.id);
    const configureDeviceModal = createModal(<ConfigureDeviceModal device={detail} />);

    setWaitingToEdit(null);

    configureDeviceModal.open()
      .then((res) => {
        // Handle update
      })
      .catch(() => {/* no-op */})
  }

  return (<div className="manage-devices">
    {Object.keys(availableTypes || {})?.length ?
      <div className="table-controls" style={{maxWidth: 480}}>
        <div className="select-action">
          <button
            disabled={!selectedType || (availableTypes[selectedType]?.children?.length && !selectedDeviceModel)}
            onClick={() => add()}
          >
            <Trans>Add</Trans>
          </button>
          <select onChange={event => setSelectedType(event.target.value)}>
            <option value={''}>
              <Trans>Select type...</Trans>
            </option>
            {Object.keys(availableTypes).map(t =>
              <option value={t} key={`device-type-select-${t}`}>{t}</option>
            )}
          </select>
          {availableTypes[selectedType]?.children.length ?
            <select
              disabled={loading}
              onChange={event => setSelectedDeviceModel(event.target.value)}
            >
              <option value={''}>
                <Trans>Select device model...</Trans>
              </option>
              {availableTypes[selectedType].children.map(t =>
                <option value={t.id} key={`device-model-select-${t.id}`}>{t.name}</option>
              )}
            </select>
          : null}
          &nbsp;<LoadingIndicator show={loading} />
        </div>
      </div>
    : null}
    <div className="table-container" style={{maxWidth: 480}}>
      <table>
        <thead>
          <tr>
            <th><Trans>Device</Trans></th>
            <th><Trans>Actions</Trans></th>
          </tr>
        </thead>
        <tbody>
          {children.map(child => {
            const childIsDuo = isDuo(child.externalId);
            if (child.type == 'Circuit') {
              // Similar to above, let's grab the parent controller if this is a Duo circuit
              // NOTE - the wrapper is just me being lazy and not grabbing the model from shallowDevices
              const parent = isDuo(device.model.externalId) ? { model: getAllChildDevicesByType(device.model, 'Controller')[0] } : device.model;
              return <CircuitRow key={`circuit-row-${child.id}`} device={child} parent={parent} />;
            }

            return (
              <tr key={`child-device-${child.id}`}>
                <td className="device-info">
                  <span className={`device-icon${childIsDuo ? ' duo' : ''}${child.type ? ` ${child.type.toLowerCase()}` : ''}`}></span>
                  <Link to={`${getDeviceUrl(devicesStore.shallowDevices.get(child.id))}${!childIsDuo ? '#network-settings' : ''}`}>
                    {child.name}
                  </Link>
                </td>
                <td>
                  <button
                    className={waitingToEdit == child.id ? 'settings processing' : 'settings'}
                    data-tooltip-content={t`Device Settings`}
                    data-tooltip-id="manage-devices-tooltip"
                    disabled={waitingToEdit == child.id || !deviceTypesStore.types.length}
                    onClick={() => edit(child)}
                  >
                    <Trans>Device Settings</Trans>
                  </button>
                  {!ancestorIsDuo && !isDuo(device.model.externalId) ? <button
                    className="delete danger"
                    data-tooltip-content={
                      !isDuo(child.externalId) && child.type != 'Controller' && child.children.length ? t`Devices with child nodes cannot be removed` : t`Remove Device`
                    }
                    data-tooltip-id="manage-devices-tooltip"
                    onClick={() => remove(child)}
                    disabled={!isDuo(child.externalId) && child.type != 'Controller' && child.children.length}
                  >
                    <Trans>Remove Device</Trans>
                  </button> : null}
                </td>
              </tr>
            );
          })}
          {!children.length ? <tr>
            <td colSpan="2"><Trans>No child devices</Trans></td>
          </tr> : null}
        </tbody>
      </table>
    </div>
    <Tooltip id="manage-devices-tooltip" place="bottom-end" />
  </div>);
}

export default ManageDevices;
